import _ from 'underscore';
import Promise from 'bluebird';
import React, { Fragment, Suspense, useMemo, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { inlineChartContainerStyle } from '@bingads-webui-campaign-react/inline-chart-style';
import { ProgressiveLoadingBlock } from '@bingads-webui-campaign-react/shell-ui-common';
import { StyledAccountsWelcomeBar } from '@bingads-webui-campaign-react/accounts-welcome-bar';
import { StyledTablePageContainer } from '@bingads-webui-campaign-react/table-page-container';
import { StyledGridViewWrapper } from '@bingads-webui-campaign-react/grid-view-wrapper';
import { TopGridPanel } from '@bingads-webui-campaign-react/top-grid-panel';
import { withGlobalFilter } from '@bingads-webui-campaign-react/global-filter-context';
import { withTablePageStateManager } from '@bingads-webui-campaign-react/table-page-state-manager';
import { withInstrumentation } from '@bingads-webui-react/hoc-with-instrumentation';
import { FilterViewManagerWithGlobalFilterContext } from '@bingads-webui-campaign-react/filter-view-manager';
import { getToolbarEditor } from '@bingads-webui-campaign-react/toolbar-plugin';
import { DisclaimerBannerWithoutTimezone, DisclaimerBannerWithInstoreWithoutTimezone } from '@bingads-webui-campaign-react/help';
import { perfMarkerPropTypes } from '@bingads-webui/performance-marker';
import { createAccountsGridForUiNext, dismissCallout, ACCOUNTS_GRID_CALLOUT_MOUNTING_DOM, getGridStyles, getCalloutStyles } from '@bingads-webui-campaign-react/accounts-grid-factory';
import { gridsType, gridsMap } from '@bingads-webui-campaign/accounts-grid';
import { dismissPopupIfExist } from '@bingads-webui-campaign/fluctuation-common';
import { ScopeSelector } from '@bingads-webui-react/scope-selector';
import { IONotifications } from '@bingads-webui-cc-react/io-notifications';
import { LoggerContextProvider } from '@bingads-webui-cc-react/instrumentation-base';
import { InlineWarningAlert } from '@bingads-webui-cc-react/alert';
import { constants } from '@bingads-webui/instrumentation';
import { withDelayedStyles } from '@bingads-webui-campaign-react/common-utils';
import { getLinkAccountButtonStyle } from '@bingads-webui-campaign-react/accounts-summary-common';
import { getFluctuationPopupStyle,
  getFluctuationTooltipStyle,
  getFluctuationLoadingStyle,
  getBudgetCenterTooltipStyle } from '@bingads-webui-campaign-react/common-styles';
import eventDispatcher from '@bingads-webui-component-legacy/event-dispatcher/index';
import { Constants } from '@bingads-webui-clientcenter/entity-utils';

import { getToolbarConfig } from './toolbar-config';
import { getColumnActionEvents } from './column-action-events';

const { EnforcementActionType } = Constants;

const Performance = ({
  classes,
  forceHideToolbar,
  i18n,
  instrumentation,
  onGridInjected,
  pageName,
  panelView,
  perfMarker,
  permissions,
  preexecuteFlag,
  preferenceService,
  renderToolbarContainer,
  resetToolbarVisibility,
  scope,
  serverContext,
  showInlineChart,
  showTopGridPanel,
  crossPageSelectPlugin,
  toolbarPlugin,
  toolbarSelectedConfig,
  toolbarUnselectedConfig,
  updateToolbar,
  siteMap,
  lazyLoadDep,
  accountsGridLegacyDepsListWebpack,
  loadInlineChartWithWebpack,
  getjsDataCampaign,
  history,
}) => {
  const [backboneGridView, setBackboneGridView] = useState(null);
  const [inlineChart, setInlineChart] = useState(null);
  let defaultGridType = gridsType.performance;
  const expertAccountPillSelected = _.get(scope, ['values', 'expertAccountPillSelected']) === 'true';
  if (!expertAccountPillSelected && serverContext.CurrentCustomer.HasUnifiedSmartAccount) {
    defaultGridType = gridsType.smartPerformance;
  }
  const [gridType, setGridType] = useState(defaultGridType);

  useEffect(() =>
    // do nothing on mounted, only dismiss possible callout on unmounted
    () => dismissCallout(), []);

  useEffect(() => {
    const pageContainer = document.querySelector('.table-page-container');

    const dismissPopup = () => dismissPopupIfExist([document.getElementById(ACCOUNTS_GRID_CALLOUT_MOUNTING_DOM)]);

    if (pageContainer) {
      pageContainer.addEventListener('scroll', dismissPopup);
    }

    return () => {
      pageContainer.removeEventListener('scroll', dismissPopup);
    };
  }, []);
  if (perfMarker) {
    // eslint-disable-next-line no-param-reassign
    perfMarker.doneCallback = () => {
      eventDispatcher.trigger('performance_grid_loaded');
    };
  }

  const accountsGridPromise = useMemo(
    () => createAccountsGridForUiNext({
      serverContext,
      permissions,
      scope,
      perfMarker,
      addDateChangeListener: _.noop,
      gridName: gridsMap[gridType].gridName,
      gridType,
      pageElementSelector: '.performance-page',
      pageName,
      preexecuteFlag,
      preferenceService,
      crossPageSelectPlugin,
      toolbarPlugin,
      i18n,
      siteMap,
      lazyLoadDep,
      accountsGridLegacyDepsListWebpack,
      getEvents: getColumnActionEvents({
        serverContext, instrumentation, scope, getjsDataCampaign, appConfig: accountsGridLegacyDepsListWebpack['component/config/index'], perfMarker,
      }),
    }),
    // Theoretically we should return a function to cleanup promises, but no page seems to be doing that right now.
    [serverContext, permissions, scope, perfMarker, gridType, pageName, preferenceService, crossPageSelectPlugin, toolbarPlugin, i18n, siteMap, lazyLoadDep, accountsGridLegacyDepsListWebpack, instrumentation, preexecuteFlag, getjsDataCampaign]
  );

  const MakeGridView = React.lazy(() => accountsGridPromise.then(({ grid, filterService }) => {
    // This lets TablePageStateManager figure out when to start performing scheduled updates.
    onGridInjected({
      deselectAll: grid.gridView.deselectAll.bind(grid.gridView),
      backboneGridView: grid.gridView,
    });

    setBackboneGridView(grid);

    const StyledGridViewWrapperWithFilterContext = withGlobalFilter(StyledGridViewWrapper);

    const InlineChart = React.lazy(() => (
      Promise.resolve()
    )
      .then(() => (loadInlineChartWithWebpack)({
        serverContext,
        gridView: { grid },
        filterService,
        chartContainer: '.performance-inline-chart',
        entityType: 'Account',
        preexecuteFlag,
        gridName: gridsMap[gridType].gridName,
      }))
      .then(inlineChartView => ({
        // React.lazy takes a function that returns a promise that resolves to a default export style component.
        default: () => inlineChartView,
      })));

    setInlineChart(<InlineChart />);

    return {
      default: () => (<StyledGridViewWrapperWithFilterContext gridView={grid} removeGridOnUmount />),
    };
  }));

  const MakeFilterBarView = React.lazy(() => accountsGridPromise.then(({ filterService, filterConfig }) => {
    const toolbarEditor = getToolbarEditor({
      toolbarConfig: { toolbarSelectedConfig, toolbarUnselectedConfig },
      updateToolbar,
    });
    const { i18n: legacyI18n, ...filterProps } = filterConfig;
    const updateToolbarBinded = _.bind(toolbarEditor.update, toolbarEditor);

    return {
      default: () => (
        <FilterViewManagerWithGlobalFilterContext
          filterService={filterService}
          // eslint-disable-next-line react/jsx-no-bind
          updateToolbar={updateToolbarBinded}
          resetToolbarVisibility={resetToolbarVisibility}
          legacyI18n={legacyI18n}
          i18n={i18n}
          permissions={permissions}
          {...filterProps}
        />
      ),
    };
  }));
  const GridView = useRef(MakeGridView);
  const FilterBarView = useRef(MakeFilterBarView);

  // Triggers full reload of grid whenever the gridType changes from Smart to Expert or vice versa
  useEffect(() => {
    GridView.current = MakeGridView;
    FilterBarView.current = MakeFilterBarView;
    // DO NOT FIX THIS LINT ERROR OR ELSE ENTIRE PAGE FREEZES
    // eslint-disable-next-line
  }, [gridType]);

  const { CurrentUser = {} } = serverContext;
  const { dynamic: { DSAEnforcement: DSAEnforcementEnabled } = {} } = permissions;
  const stickyTopOfGridContent = (
    <Fragment>
      {renderToolbarContainer()}
      <FilterBarView.current />
      {DSAEnforcementEnabled && CurrentUser.EnforcementActionId === EnforcementActionType.SuspendAccountCreation
        ? (
          <div id="dsa-enforcement-warning" className={classes.dsaSuspensionWarning}>
            <InlineWarningAlert>
              {i18n.getString(_TL_('Your manager account is currently under a Strike 2 penalty and you are suspended from creating new accounts. Details have been shared on your admin email.'))}
            </InlineWarningAlert>
          </div>)
        : null}
    </Fragment>
  );

  // The choice between UP Smart and Expert performance grids only appears if customer has both account types, otherwise it's hidden
  const showAccountsModeSelector = serverContext.CurrentCustomer.HasUnifiedSmartAccount
    && serverContext.CurrentCustomer.HasExpertAccount;

  const onModeChange = ({ index }) => {
    resetToolbarVisibility({ forceHideToolbar: false });
    if (index === 0) {
      setGridType(gridsType.smartPerformance);
    } else {
      setGridType(gridsType.performance);
    }
  };

  const logger = useMemo(() => ({
    trace: (scenarioName, message) => {
      instrumentation.writeRawLog({
        logCategory: constants.LOGCATEGORY.TRACE,
        api: scenarioName,
        message,
        entryType: constants.ENTRYTYPE.MESSAGE,
      });
    },
  }), [instrumentation]);

  const topOfGridContent = React.useMemo(() => (
    <Fragment>
      <StyledAccountsWelcomeBar
        customerName={serverContext.CurrentCustomer.Name}
        permissions={permissions}
        i18n={i18n}
      />
      {showAccountsModeSelector ? (
        <ScopeSelector
          customClasses={{
            scopeSelectorCustomClass: 'accounts-mode-selector',
            pillControlCustomClass: null, // TODO: styling
          }}
          hideSubMenuSeparator="true"
          pillControlParams={{
            levelIndex: gridType === gridsType.performance ? 1 : 0,
            onItemChange: ({ index }) => onModeChange({ index }),
            levelItems: [
              {
                name: i18n.getString(_TL_('Smart accounts')),
                value: 0,
              },
              {
                name: i18n.getString(_TL_('Expert accounts')),
                value: 1,
              }],
          }}
        />
      ) : null}
      <LoggerContextProvider logger={logger}>
        <IONotifications uid={serverContext.CurrentUser.Id} cid={serverContext.CurrentCustomer.Id} history={history} siteMap={siteMap} containerClass={classes.IONotificationsContainer} />
      </LoggerContextProvider>
      <div className={classNames(['campaignContentContainer', classes.inlineChartContainer])}>
        <section className="performance-inline-chart">
          <Suspense fallback={ProgressiveLoadingBlock}>
            {showInlineChart && inlineChart}
          </Suspense>
        </section>
      </div>
      {showTopGridPanel &&
        <TopGridPanel
          panelView={panelView}
          customClasses={{
            topGridPanelCustomStyles: classes.topGridPanelCustomStyles,
          }}
        />
      }
    </Fragment>), [serverContext.CurrentCustomer.Name, serverContext.CurrentCustomer.Id, serverContext.CurrentUser.Id, permissions, i18n, showAccountsModeSelector, gridType, logger, history, siteMap, classes.IONotificationsContainer, classes.inlineChartContainer, classes.topGridPanelCustomStyles, showInlineChart, inlineChart, showTopGridPanel, panelView, onModeChange]);

  return (
    <Suspense fallback={<ProgressiveLoadingBlock />}>
      <StyledTablePageContainer
        topOfGridContent={topOfGridContent}
        stickyTopOfGridContent={stickyTopOfGridContent}
        gridView={<GridView.current />}
        pgridView={backboneGridView ? backboneGridView.gridView : null}
        customClasses={{
          tablePageContainer: classNames('performance-page', classes.gridContainer),
          gridViewContainer: classNames('performance-table', classes.grid),
        }}
        CustomDisclaimerBanner={_.get(permissions, ['IsInStoreVisitGoalEnabled']) ? DisclaimerBannerWithInstoreWithoutTimezone : DisclaimerBannerWithoutTimezone}
        scrollToTop={forceHideToolbar && showTopGridPanel}
      />
    </Suspense>
  );
};

Performance.propTypes = {
  serverContext: PropTypes.shape({
    CurrentCustomer: PropTypes.shape({
      HasUnifiedSmartAccount: PropTypes.bool.isRequired,
      HasExpertAccount: PropTypes.bool.isRequired,
      Name: PropTypes.string.isRequired,
      Id: PropTypes.number.isRequired,
    }),
    CurrentUser: PropTypes.shape({
      Id: PropTypes.number.isRequired,
    }),
  }).isRequired,
  permissions: PropTypes.shape({}).isRequired,

  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  instrumentation: PropTypes.shape({
    writeRawLog: PropTypes.func,
  }).isRequired,
  perfMarker: perfMarkerPropTypes.isRequired,

  // Props from HOC withPreference (in ui-next/app-content)
  preferenceService: PropTypes.shape().isRequired,

  // Props from HOC withScope (in ui-next/app-content)
  scope: PropTypes.shape({
    values: PropTypes.shape({}),
  }).isRequired,
  siteMap: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
  })).isRequired,

  // Props from HOC withDefaultStyles
  classes: PropTypes.shape().isRequired,

  // Props from HOC withTablePageStateManager
  crossPageSelectPlugin: PropTypes.shape({
    plugin: PropTypes.func.isRequired,
  }).isRequired,
  toolbarPlugin: PropTypes.shape({
    plugin: PropTypes.func.isRequired,
  }).isRequired,
  renderToolbarContainer: PropTypes.func.isRequired,
  toolbarSelectedConfig: PropTypes.shape().isRequired,
  toolbarUnselectedConfig: PropTypes.shape().isRequired,
  // showToolbarSelected: PropTypes.bool.isRequired,
  // showToolbarUnselected: PropTypes.bool.isRequired,
  // This prop is used by TablePageStateManager to force hide the inline chart when the top of grid panel is shown.
  showInlineChart: PropTypes.bool.isRequired,
  // forceHideInlineChart: PropTypes.bool.isRequired,
  showTopGridPanel: PropTypes.bool.isRequired,
  // showTopOfGridPanel: PropTypes.func.isRequired,
  onGridInjected: PropTypes.func.isRequired,
  updateToolbar: PropTypes.func.isRequired,
  resetToolbarVisibility: PropTypes.func.isRequired,
  forceHideToolbar: PropTypes.bool.isRequired,
  panelView: PropTypes.func.isRequired,
  // forceResetScrollFlag: PropTypes.bool.isRequired,
  pageName: PropTypes.string,
  preexecuteFlag: PropTypes.bool,
  lazyLoadDep: PropTypes.func,
  // eslint-disable-next-line
  accountsGridLegacyDepsListWebpack: PropTypes.object,
  loadInlineChartWithWebpack: PropTypes.func,
  getjsDataCampaign: PropTypes.func,
  history: PropTypes.shape({
    push: PropTypes.func,
  }).isRequired,
};

Performance.defaultProps = {
  pageName: '',
  preexecuteFlag: false,
  lazyLoadDep: undefined,
  accountsGridLegacyDepsListWebpack: null,
  loadInlineChartWithWebpack: undefined,
  getjsDataCampaign: undefined,
};

const getDelayedPerformanceStyles = () => (
  import(/* webpackMode: 'eager' */'./performance-page-styles')
    .then(({ getPerformanceStyles }) => ({
      getDelayedStyles: getPerformanceStyles,
    }))
);
const getDefaultStyles = (components, { palette = {} }) => ({
  inlineChartContainer: inlineChartContainerStyle(palette),
  gridContainer: {
    ...getLinkAccountButtonStyle(components, { palette }),
  },
  grid: {
    ...getGridStyles(components, { palette }),

    // column width override
    '&& .table-container colgroup.column-group .col-AccountName': {
      width: 300,
      minWidth: 300,
    },
  },
  '@global': {
    ...getCalloutStyles(components, { palette }),
    ...getFluctuationPopupStyle({ palette }),
    ...getFluctuationTooltipStyle(),
    ...getFluctuationLoadingStyle(),
    ...getBudgetCenterTooltipStyle(),
  },
});

export const StyledPerformance = _.compose(
  _.partial(withDelayedStyles, _, getDefaultStyles, getDelayedPerformanceStyles, 'performance_grid_loaded'),
  withTablePageStateManager({ getToolbarConfig }),
  withInstrumentation
)(Performance);
